import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { uploadAPI } from '../api/client'
import { publicAssetUrl } from '../utils/assetUrl'
import ListingPhotoCropModal from './ListingPhotoCropModal'

export interface ListingImageRow {
  id: number
  thumbnail_url: string
  display_url?: string
  is_primary: boolean
}

interface MyListingPhotosManagerProps {
  listingId: number
  token: string
  images: ListingImageRow[]
  isSold: boolean
  onChange: () => void
}

export default function MyListingPhotosManager({
  listingId,
  token,
  images,
  isSold,
  onChange,
}: MyListingPhotosManagerProps) {
  const { t } = useTranslation()
  const [localImages, setLocalImages] = useState(images)
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const [cropQueue, setCropQueue] = useState<File[]>([])
  const [cropFileName, setCropFileName] = useState('photo.jpg')
  const [cropEdit, setCropEdit] = useState<{ imageId: number; url: string; name: string } | null>(null)
  const [cropSource, setCropSource] = useState<File | string | null>(null)
  const [dragPhotoIdx, setDragPhotoIdx] = useState<number | null>(null)
  const [dragOverPhotoIdx, setDragOverPhotoIdx] = useState<number | null>(null)

  useEffect(() => {
    setLocalImages(images)
  }, [images])

  useEffect(() => {
    if (lightboxIdx == null || !trackRef.current) return
    const el = trackRef.current
    const w = el.clientWidth
    if (w > 0) el.scrollLeft = lightboxIdx * w
  }, [lightboxIdx])

  const sorted = [...localImages].sort((a, b) => {
    if (a.is_primary) return -1
    if (b.is_primary) return 1
    return 0
  })

  function displayUrl(img: ListingImageRow): string {
    return publicAssetUrl(img.display_url || img.thumbnail_url || '')
  }

  function thumbUrl(img: ListingImageRow): string {
    return publicAssetUrl(img.display_url || img.thumbnail_url || '')
  }

  async function deleteImage(imageId: number) {
    if (!window.confirm(t('myListings.photosDeleteConfirm'))) return
    setBusy(true)
    setError('')
    try {
      await uploadAPI.deleteListingImage(imageId, token)
      setLocalImages((prev) => prev.filter((i) => i.id !== imageId))
      setLightboxIdx(null)
      onChange()
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : null
      setError(msg || t('myListings.photosError'))
    } finally {
      setBusy(false)
    }
  }

  async function reorderPhotos(from: number, to: number) {
    if (from === to || from < 0 || to < 0 || busy) return
    const order = [...sorted]
    if (from >= order.length || to >= order.length) return
    const [item] = order.splice(from, 1)
    order.splice(to, 0, item)
    setBusy(true)
    setError('')
    try {
      const res = await uploadAPI.reorderListingImages(
        listingId,
        order.map((i) => i.id),
        token,
      )
      const next = (res.data as { images?: ListingImageRow[] }).images
      if (next?.length) setLocalImages(next)
      onChange()
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : null
      setError(msg || t('myListings.photosError'))
    } finally {
      setBusy(false)
    }
  }

  function onPhotoDragStart(index: number, e: React.DragEvent) {
    if (isSold || (e.target as HTMLElement).closest('button')) {
      e.preventDefault()
      return
    }
    setDragPhotoIdx(index)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', String(index))
  }

  function onPhotoDragOver(index: number, e: React.DragEvent) {
    if (isSold) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (dragOverPhotoIdx !== index) setDragOverPhotoIdx(index)
  }

  function onPhotoDrop(index: number, e: React.DragEvent) {
    if (isSold) return
    e.preventDefault()
    const from =
      dragPhotoIdx ??
      (() => {
        const raw = e.dataTransfer.getData('text/plain')
        const parsed = Number.parseInt(raw, 10)
        return Number.isFinite(parsed) ? parsed : null
      })()
    if (from != null) void reorderPhotos(from, index)
    setDragPhotoIdx(null)
    setDragOverPhotoIdx(null)
  }

  function onPhotoDragEnd() {
    setDragPhotoIdx(null)
    setDragOverPhotoIdx(null)
  }

  async function setCover(imageId: number) {
    if (sorted[0]?.id === imageId) return
    const order = [imageId, ...sorted.filter((i) => i.id !== imageId).map((i) => i.id)]
    setBusy(true)
    setError('')
    try {
      const res = await uploadAPI.reorderListingImages(listingId, order, token)
      const next = (res.data as { images?: ListingImageRow[] }).images
      if (next?.length) setLocalImages(next)
      onChange()
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : null
      setError(msg || t('myListings.photosError'))
    } finally {
      setBusy(false)
    }
  }

  async function replaceImage(imageId: number, file: File) {
    const oldIndex = sorted.findIndex((i) => i.id === imageId)
    setBusy(true)
    setError('')
    try {
      await uploadAPI.deleteListingImage(imageId, token)
      const up = await uploadAPI.uploadListingImage(listingId, file, token)
      const newId = (up.data as { id?: number }).id
      if (newId != null && oldIndex >= 0) {
        const order = sorted.filter((i) => i.id !== imageId).map((i) => i.id)
        order.splice(oldIndex, 0, newId)
        const res = await uploadAPI.reorderListingImages(listingId, order, token)
        const next = (res.data as { images?: ListingImageRow[] }).images
        if (next?.length) setLocalImages(next)
      }
      onChange()
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : null
      setError(msg || t('myListings.photosError'))
    } finally {
      setBusy(false)
    }
  }

  async function uploadCroppedFile(file: File) {
    setBusy(true)
    setError('')
    try {
      await uploadAPI.uploadListingImage(listingId, file, token)
      onChange()
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : null
      setError(msg || t('myListings.photosError'))
    } finally {
      setBusy(false)
    }
  }

  function closeCrop() {
    setCropSource(null)
    setCropQueue([])
    setCropEdit(null)
  }

  async function onCropConfirm(file: File) {
    if (cropEdit) {
      await replaceImage(cropEdit.imageId, file)
      closeCrop()
      return
    }
    await uploadCroppedFile(file)
    if (cropQueue.length > 0) {
      const [next, ...rest] = cropQueue
      setCropQueue(rest)
      setCropFileName(next.name)
      setCropSource(next)
      return
    }
    closeCrop()
  }

  async function onFilesSelected(files: FileList | null) {
    if (!files?.length || isSold) return
    const remaining = 10 - localImages.length
    if (remaining <= 0) return
    const batch = Array.from(files).slice(0, remaining)
    setBusy(true)
    setError('')
    try {
      for (const file of batch) {
        await uploadAPI.uploadListingImage(listingId, file, token)
      }
      onChange()
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : null
      setError(msg || t('myListings.photosError'))
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  function startEditPhoto(img: ListingImageRow) {
    const url = displayUrl(img)
    setCropQueue([])
    setCropEdit({
      imageId: img.id,
      url,
      name: `listing-${listingId}-${img.id}.jpg`,
    })
    setCropFileName(`listing-${listingId}-${img.id}.jpg`)
    setBusy(true)
    void fetch(url, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error('fetch failed')
        return res.blob()
      })
      .then((blob) => {
        const file = new File([blob], `listing-${listingId}-${img.id}.jpg`, {
          type: blob.type || 'image/jpeg',
        })
        setCropSource(file)
      })
      .catch(() => {
        setCropEdit(null)
        setError(t('listing.photoCrop.loadError'))
      })
      .finally(() => setBusy(false))
  }

  function onLightboxScroll(e: React.UIEvent<HTMLDivElement>) {
    const el = e.currentTarget
    const w = el.clientWidth
    if (w <= 0 || sorted.length <= 1) return
    const idx = Math.round(el.scrollLeft / w)
    if (idx !== lightboxIdx) setLightboxIdx(idx)
  }

  const canAddPhoto = !isSold && sorted.length < 10

  if (sorted.length === 0 && isSold) return null

  return (
    <div className="seller-listing-photos">
      <h4 className="seller-listing-subheading">{t('myListings.photosHeading')}</h4>

      {error && <p className="error-text seller-listing-photos__error">{error}</p>}

      {sorted.length > 1 && !isSold ? (
        <p className="seller-listing-photos__reorder-hint">{t('listing.photoDragHint')}</p>
      ) : null}

      <ul className="seller-listing-photos__grid">
        {sorted.map((img, idx) => (
          <li
            key={img.id}
            className={[
              'seller-listing-photos__item',
              dragPhotoIdx === idx ? 'seller-listing-photos__item--dragging' : '',
              dragOverPhotoIdx === idx && dragPhotoIdx !== idx
                ? 'seller-listing-photos__item--drag-over'
                : '',
              idx === 0 ? 'seller-listing-photos__item--primary' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            draggable={!isSold}
            onDragStart={(e) => onPhotoDragStart(idx, e)}
            onDragOver={(e) => onPhotoDragOver(idx, e)}
            onDrop={(e) => onPhotoDrop(idx, e)}
            onDragEnd={onPhotoDragEnd}
          >
            <button
              type="button"
              className="seller-listing-photos__thumb-btn"
              onClick={() => setLightboxIdx(idx)}
              aria-label={t('myListings.photosOpen')}
            >
              <img src={thumbUrl(img)} alt="" draggable={false} />
              {idx === 0 ? (
                <span className="seller-listing-photos__cover-badge">{t('listing.photoPrimaryPreview')}</span>
              ) : null}
            </button>
            {!isSold && (
              <div className="seller-listing-photos__actions">
                <button
                  type="button"
                  className="seller-listing-photos__action"
                  disabled={busy}
                  onClick={() => startEditPhoto(img)}
                >
                  {t('myListings.photosEdit')}
                </button>
                {!img.is_primary && (
                  <button
                    type="button"
                    className="seller-listing-photos__action"
                    disabled={busy}
                    onClick={() => setCover(img.id)}
                  >
                    {t('myListings.photosSetCover')}
                  </button>
                )}
                <button
                  type="button"
                  className="seller-listing-photos__action seller-listing-photos__action--danger"
                  disabled={busy}
                  onClick={() => deleteImage(img.id)}
                >
                  {t('myListings.photosDelete')}
                </button>
              </div>
            )}
          </li>
        ))}
        {canAddPhoto ? (
          <li className="seller-listing-photos__item">
            <button
              type="button"
              className="seller-listing-photos__add-tile"
              disabled={busy}
              onClick={() => fileRef.current?.click()}
            >
              <span className="seller-listing-photos__add-plus" aria-hidden="true">
                +
              </span>
              <span>{t('myListings.photosAdd')}</span>
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/*"
              multiple
              className="sr-only"
              onChange={(e) => onFilesSelected(e.target.files)}
            />
          </li>
        ) : null}
      </ul>

      {lightboxIdx != null && sorted.length > 0 && (
        <div
          className="seller-photo-lightbox"
          role="dialog"
          aria-modal="true"
          onClick={() => setLightboxIdx(null)}
        >
          <button
            type="button"
            className="seller-photo-lightbox__close"
            aria-label={t('common.close', 'Uždaryti')}
            onClick={() => setLightboxIdx(null)}
          >
            ×
          </button>
          <div
            ref={trackRef}
            className="seller-photo-lightbox__track"
            onClick={(e) => e.stopPropagation()}
            onScroll={onLightboxScroll}
          >
            {sorted.map((img) => (
              <div key={img.id} className="seller-photo-lightbox__slide">
                <img src={displayUrl(img)} alt="" className="seller-photo-lightbox__img" />
              </div>
            ))}
          </div>
          {sorted.length > 1 && (
            <p className="seller-photo-lightbox__counter">
              {(lightboxIdx ?? 0) + 1} / {sorted.length}
            </p>
          )}
        </div>
      )}

      <ListingPhotoCropModal
        open={cropSource != null}
        source={cropSource}
        fileName={cropEdit?.name ?? cropFileName}
        onConfirm={(file) => void onCropConfirm(file)}
        onCancel={closeCrop}
      />
    </div>
  )
}
